import React, { useCallback, useState } from 'react';

import { FiltrosCompras, FiltrosDaLista, fetchPedidosDoMes } from '../../../api/comprasDashboardApi';
import ModalDeDetalhe, { SUFIXO_FILTRADO, useDetalhePaginado } from './ModalDeDetalhe';
import { SITUACOES_DE_PEDIDO } from '../situacoes';
import SeloDeComentarios from '../../../components/comentarios/SeloDeComentarios';
import DetalheDoPedidoModal from './DetalheDoPedidoModal';
import { formatarDataPura } from '../formatarData';

/**
 * Os pedidos de compra de UM mês — o detalhamento do clique numa coluna do gráfico de evolução.
 *
 * Monta sobre o casco compartilhado (`ModalDeDetalhe` + `useDetalhePaginado`) desde a at-95: era
 * a primeira de três cópias do mesmo modal, com os quatro estados e a paginação reescritos à mão.
 *
 * 🔴 A lista usa o MESMO predicado da barra: emissão no mês e situação **não cancelada**. Listar
 * tudo do mês traria os cancelados e a lista mostraria mais pedidos do que a coluna que a abriu —
 * e o gráfico diz, no rodapé, que os cancelados não entram na conta.
 *
 * 🔴 **Recebe os filtros do painel desde a at-115**: a coluna do gráfico aplica comprador e centro
 * de custo (pelo elo pedido → negociação → requisição, da at-109), e lista que não os aplicasse
 * somaria mais do que a coluna que a abriu.
 *
 * ⚠️ O período do filtro **não** entra: o recorte de tempo deste clique é o mês da coluna. O
 * backend aceita `de`/`ate` e os ignora, para o frontend poder mandar o filtro inteiro sem tomar
 * 400 do `forbidNonWhitelisted`.
 *
 * 🔴 **Não existe aviso de divergência com a coluna, e a ausência é deliberada** (decisão do Victor,
 * 11/09/2026): o acordo é garantido por uma definição só de "a que mês o pedido pertence"
 * (`chaveDoMes`/`janelaDoMes`, no service, em UTC), com sentinela que compara as duas.
 */

const POR_PAGINA = 25;

interface Props {
  mes: string;
  /** Os filtros do painel — at-115. Ver o cabeçalho: o período deles é ignorado pelo backend. */
  filtros?: FiltrosCompras;
  onFechar: () => void;
}

/**
 * 🔴 Quatro colunas, não seis (at-110, pedido do Victor: "eu queria no máximo 4").
 *
 * Situação, previsão de entrega e operação saíram para o DETALHE, a um clique no número.
 */
const COLUNAS = ['Pedido', 'Emissão', 'Fornecedor', 'Valor'];

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

function formatarMes(mes: string): string {
  const [ano, mm] = mes.split('-');
  return `${MESES[Number(mm) - 1] ?? mm} de ${ano}`;
}

const MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export default function PedidosDoMesModal({ mes, filtros, onFechar }: Props) {
  // at-110: o pedido aberto em detalhe. Montagem condicional, para o filho recomeçar do zero.
  const [pedidoAberto, setPedidoAberto] = useState<{ id: string; numero: string | null } | null>(null);

  const carregarDados = useCallback(
    (pagina: number, daLista: FiltrosDaLista) => fetchPedidosDoMes(mes, pagina, POR_PAGINA, filtros, daLista),
    [mes, filtros],
  );
  const { dados, carregando, erro, pagina, setPagina, filtrosDaLista, aplicarFiltrosDaLista, filtrado } =
    useDetalhePaginado(carregarDados);

  const total = dados?.total ?? 0;
  const pedidos = dados?.pedidos ?? [];
  const somaDaPagina = pedidos.reduce((s, p) => s + p.total, 0);

  return (
    <>
      <ModalDeDetalhe
        titulo={`Pedidos de ${formatarMes(mes)}`}
        legenda={
          <>
            {carregando
              ? 'Carregando...'
              : erro
                ? 'Não foi possível carregar'
                : `${total.toLocaleString('pt-BR')} ${total === 1 ? 'pedido' : 'pedidos'}`}
            {' · cancelados não entram na conta'}
            {filtrado ? SUFIXO_FILTRADO : ''}
          </>
        }
        carregando={carregando}
        erro={erro}
        erroTexto="Não foi possível carregar os pedidos deste mês. Isto não significa que não houve pedidos — tente de novo e, se persistir, acione a TI."
        vazio={pedidos.length === 0}
        vazioTexto={
          filtrado
            ? 'Nenhum pedido deste mês com os filtros da lista. Ajuste ou limpe os filtros.'
            : 'Nenhum pedido neste mês.'
        }
        colunas={COLUNAS}
        classeTabela="dcr-tabela-pedidos"
        paginacao={{ pagina, ultimaPagina: Math.max(1, Math.ceil(total / POR_PAGINA)), onMudar: setPagina }}
        /*
          ⚠️ A soma é DESTA PÁGINA, e o texto diz isso. Chamá-la de "total do mês" seria falso: o
          valor do mês inteiro está no gráfico, e somar 25 linhas aqui daria outro número.
        */
        rodape={<span>Soma desta página: {MOEDA.format(somaDaPagina)}</span>}
        travarFechamento={Boolean(pedidoAberto)}
        filtroDaLista={{
          campos: ['busca', 'situacoes', 'placa', 'oficina', 'comprador', 'solicitante', 'fornecedor', 'periodo'],
          situacoes: SITUACOES_DE_PEDIDO,
          rotuloDaBusca: 'número, placa, fornecedor, comprador ou oficina',
          rotuloDoPeriodo: 'Emissão do pedido',
          aplicados: filtrosDaLista,
          onAplicar: aplicarFiltrosDaLista,
        }}
        onFechar={onFechar}
      >
        {pedidos.map((p) => (
          <div key={p.id} className="dcr-linha">
            <div className="dcr-celula" title={p.numero ?? ''}>
              {/* O número abre o detalhe: situação, previsão, operação, a origem e os itens. */}
              <button
                type="button"
                className="dcr-celula-link"
                onClick={() => setPedidoAberto({ id: p.id, numero: p.numero })}
              >
                {p.numero ?? 'sem número'}
              </button>
              <SeloDeComentarios total={p.comentarios} />
            </div>
            {/* 🔴 A emissão do pedido é data PURA (meia-noite UTC): `new Date` a mostraria um dia antes. */}
            <div className="dcr-celula">{formatarDataPura(p.emissao)}</div>
            <div className="dcr-celula" title={p.fornecedorNome ?? ''}>
              {p.fornecedorNome ?? <span className="dcr-ausente">fornecedor não sincronizado</span>}
            </div>
            <div className="dcr-celula">{MOEDA.format(p.total)}</div>
          </div>
        ))}
      </ModalDeDetalhe>

      {pedidoAberto && (
        <DetalheDoPedidoModal
          pedidoId={pedidoAberto.id}
          numero={pedidoAberto.numero}
          onFechar={() => setPedidoAberto(null)}
        />
      )}
    </>
  );
}
